import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  fetchInterviewHistory,
  scheduleInterview,
  rescheduleInterview,
  startInterview,
  cancelInterview,
  noShowInterview,
  completeInterview,
} from '@/modules/hrPortal/store/interview.thunk';

import type { RootState } from '@/store/store';
import type {
  ScheduleInterviewPayload,
  RescheduleInterviewPayload,
} from '@/modules/hrPortal/types/interview.types';

export default function useInterviews(
  applicationId: string,
  autoFetch = true
) {
  const dispatch = useDispatch<any>();

  const {
    interviews,
    loading,
    error,
    scheduling,
    rescheduling,
    updating,
  } = useSelector((state: RootState) => state.interview);

  useEffect(() => {
    if (!autoFetch || !applicationId) {
      return;
    }

    dispatch(fetchInterviewHistory(applicationId));
  }, [applicationId, autoFetch, dispatch]);

  const refreshInterviews = async () => {
    if (!applicationId) {
      return;
    }

    await dispatch(fetchInterviewHistory(applicationId)).unwrap();
  };

  const createInterview = async (
    payload: ScheduleInterviewPayload
  ) => {
    return dispatch(scheduleInterview(payload)).unwrap();
  };

  const updateInterviewSchedule = async (
    interviewId: string,
    payload: RescheduleInterviewPayload
  ) => {
    return dispatch(
      rescheduleInterview({
        interviewId,
        payload,
      })
    ).unwrap();
  };

  const beginInterview = async (interviewId: string) => {
    return dispatch(startInterview(interviewId)).unwrap();
  };

  const cancelScheduledInterview = async (
    interviewId: string,
    reason: string
  ) => {
    return dispatch(
      cancelInterview({
        interviewId,
        reason,
      })
    ).unwrap();
  };

  const markInterviewNoShow = async (
    interviewId: string,
    reason: string
  ) => {
    return dispatch(
      noShowInterview({
        interviewId,
        reason,
      })
    ).unwrap();
  };

  const finishInterview = async (
    interviewId: string,
    result: 'PASSED' | 'FAILED',
    remarks?: string
  ) => {
    return dispatch(
      completeInterview({
        interviewId,
        result,
        remarks,
      })
    ).unwrap();
  };

  return {
    interviews,
    loading,
    error,
    scheduling,
    rescheduling,
    updating,
    refreshInterviews,
    createInterview,
    updateInterviewSchedule,
    beginInterview,
    cancelScheduledInterview,
    markInterviewNoShow,
    finishInterview,
  };
}